import type {
  Booking,
  Client,
  ClientChallenge,
  ClientPackage,
  FunnelAnalytics,
  Lead,
  LeadStage,
  Measurement,
  RetentionAnalytics,
  Task,
  Trainer,
} from '@coachapp/shared';
import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './baseQuery';
import type { ApiMetaArg } from './requestMeta';

export interface ConvertLeadResult {
  lead: Lead;
  client: { id: string; name: string; login?: string; email?: string; status: string };
  password: string;
}

export interface InviteClientResult {
  client: Client;
  password: string;
}

export interface ClientCoinsData {
  coinBalance: number;
  coinsEnabled: boolean;
  history: import('@coachapp/shared').CoinTransaction[];
}

export type TrainerProfile = Pick<Trainer, 'id' | 'name' | 'email' | 'workingHours' | 'createdAt'>;

export interface Exercise {
  id?: string;
  name: string;
  sets: string;
  reps: string;
  restSeconds?: string;
  notes?: string;
  movementId?: string | null;
  videoId?: string | null;
}

export interface Movement {
  id: string;
  trainerId: string;
  name: string;
  currentVideoId: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface MovementMatch {
  name: string;
  movement: Pick<Movement, 'id' | 'name' | 'currentVideoId'> | null;
}

export type VideoKind = 'library' | 'active' | 'archive' | 'unattached';

export interface VideoListItem {
  id: string;
  createdAt: string;
  durationSec: number | null;
  sizeBytes: number | null;
  label: string;
  kind: VideoKind;
}

export type TrainingPlanStatus = 'ACTIVE' | 'ARCHIVED';

export interface TrainingPlan {
  id: string;
  clientId: string;
  trainerId: string;
  name: string;
  status: TrainingPlanStatus;
  createdByAi: boolean;
  createdAt: string;
  updatedAt?: string | null;
  exerciseCount?: number;
}

export interface TrainingPlanWithExercises extends TrainingPlan {
  exercises: Exercise[];
}

export const coachApi = createApi({
  reducerPath: 'coachApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'Clients',
    'Client',
    'Bookings',
    'Booking',
    'Tasks',
    'Task',
    'Leads',
    'Lead',
    'Measurements',
    'Measurement',
    'TrainingPlans',
    'TrainingPlan',
    'Challenges',
    'ClientCoins',
    'ClientPackages',
    'Trainer',
    'Retention',
    'Funnel',
    'Movements',
    'Videos',
  ],
  endpoints: (builder) => ({
    getClients: builder.query<Client[], ApiMetaArg | void>({
      query: (arg) => ({ url: '/clients', meta: arg?.meta }),
      providesTags: ['Clients'],
    }),
    getClient: builder.query<Client, { id: string } & ApiMetaArg>({
      query: ({ id, meta }) => ({ url: `/clients/${id}`, meta }),
      providesTags: (_result, _error, { id }) => [{ type: 'Client', id }],
    }),
    updateClient: builder.mutation<Client, { id: string; data: Partial<Client> }>({
      query: ({ id, data }) => ({ url: `/clients/${id}`, method: 'PATCH', data }),
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          coachApi.util.updateQueryData('getClient', { id }, (draft) => {
            Object.assign(draft, data);
          }),
        );
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            coachApi.util.updateQueryData('getClient', { id }, (draft) => {
              Object.assign(draft, updated);
            }),
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [],
    }),
    inviteClient: builder.mutation<
      InviteClientResult,
      { login: string; name: string; email?: string }
    >({
      query: (data) => ({ url: '/auth/invite', method: 'POST', data }),
      invalidatesTags: ['Clients'],
    }),

    getBookings: builder.query<Booking[], ApiMetaArg | void>({
      query: (arg) => ({ url: '/bookings', meta: arg?.meta }),
      providesTags: ['Bookings'],
    }),
    createBooking: builder.mutation<Booking, Partial<Booking>>({
      query: (data) => ({ url: '/bookings', method: 'POST', data }),
      invalidatesTags: ['Bookings'],
    }),
    updateBooking: builder.mutation<Booking, { id: string; data: Partial<Booking> }>({
      query: ({ id, data }) => ({ url: `/bookings/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['Bookings'],
    }),
    deleteBooking: builder.mutation<void, string>({
      query: (id) => ({ url: `/bookings/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Bookings'],
    }),

    getTasks: builder.query<
      Task[],
      { status?: string; clientId?: string } & ApiMetaArg | void
    >({
      query: (arg) => ({
        url: '/tasks',
        params: arg ? { status: arg.status, clientId: arg.clientId } : undefined,
        meta: arg?.meta,
      }),
      providesTags: ['Tasks'],
    }),
    createTask: builder.mutation<
      Task,
      { title: string; clientId?: string; description?: string; dueAt?: string }
    >({
      query: (data) => ({ url: '/tasks', method: 'POST', data }),
      invalidatesTags: ['Tasks'],
    }),
    updateTask: builder.mutation<
      Task,
      {
        id: string;
        data: Partial<{
          title: string;
          clientId: string | null;
          description: string;
          dueAt: string | null;
        }>;
      }
    >({
      query: ({ id, data }) => ({ url: `/tasks/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['Tasks'],
    }),
    completeTask: builder.mutation<Task, string>({
      query: (id) => ({ url: `/tasks/${id}/complete`, method: 'POST' }),
      invalidatesTags: ['Tasks'],
    }),
    deleteTask: builder.mutation<void, string>({
      query: (id) => ({ url: `/tasks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Tasks'],
    }),

    getLeads: builder.query<Lead[], ApiMetaArg | void>({
      query: (arg) => ({ url: '/leads', meta: arg?.meta }),
      providesTags: ['Leads'],
    }),
    createLead: builder.mutation<
      Lead,
      { name: string; email?: string; phone?: string; source?: string; notes?: string }
    >({
      query: (data) => ({ url: '/leads', method: 'POST', data }),
      invalidatesTags: ['Leads', 'Funnel'],
    }),
    updateLead: builder.mutation<
      Lead,
      {
        id: string;
        data: Partial<{
          name: string;
          email: string;
          phone: string;
          source: string;
          notes: string;
          stage: LeadStage;
          lostReason: string;
        }>;
      }
    >({
      query: ({ id, data }) => ({ url: `/leads/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['Leads', 'Funnel'],
    }),
    convertLead: builder.mutation<ConvertLeadResult, string>({
      query: (id) => ({ url: `/leads/${id}/convert`, method: 'POST' }),
      invalidatesTags: ['Leads', 'Clients', 'Funnel'],
    }),
    deleteLead: builder.mutation<void, string>({
      query: (id) => ({ url: `/leads/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Leads', 'Funnel'],
    }),

    getMeasurements: builder.query<Measurement[], { clientId: string } & ApiMetaArg>({
      query: ({ clientId, meta }) => ({ url: `/measurements/${clientId}`, meta }),
      providesTags: (_result, _error, { clientId }) => [
        { type: 'Measurements', id: clientId },
      ],
    }),
    createMeasurement: builder.mutation<
      Measurement,
      { clientId: string; data: Record<string, unknown> }
    >({
      query: ({ clientId, data }) => ({
        url: `/measurements/${clientId}`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Measurements', id: clientId },
      ],
    }),
    updateMeasurement: builder.mutation<
      Measurement,
      { id: string; clientId: string; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({ url: `/measurements/${id}`, method: 'PATCH', data }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Measurements', id: clientId },
      ],
    }),
    deleteMeasurement: builder.mutation<void, { id: string; clientId: string }>({
      query: ({ id }) => ({ url: `/measurements/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Measurements', id: clientId },
      ],
    }),
    generateInsight: builder.mutation<
      { content: string },
      { role: string; data: Record<string, unknown> }
    >({
      query: (data) => ({
        url: '/ai/generate-insight',
        method: 'POST',
        data,
        meta: { timeout: 180_000 },
      }),
    }),
    generatePlan: builder.mutation<
      { name: string; exercises: Exercise[] },
      { clientId: string; goal: string }
    >({
      query: (data) => ({
        url: '/ai/generate-plan',
        method: 'POST',
        data,
        meta: { timeout: 180_000 },
      }),
    }),

    getTrainingPlans: builder.query<TrainingPlan[], ApiMetaArg | void>({
      query: (arg) => ({ url: '/training-plans', meta: arg?.meta }),
      providesTags: ['TrainingPlans'],
      serializeQueryArgs: () => 'training-plans',
    }),
    getTrainingPlan: builder.query<TrainingPlanWithExercises, string>({
      query: (id) => ({ url: `/training-plans/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'TrainingPlan', id }],
    }),
    createTrainingPlan: builder.mutation<
      TrainingPlan,
      { clientId: string; name: string; exercises?: Exercise[] }
    >({
      query: ({ clientId, ...data }) => ({
        url: '/training-plans',
        method: 'POST',
        data: { clientId, ...data },
      }),
      async onQueryStarted({ clientId, name, exercises }, { dispatch, queryFulfilled }) {
        const optimisticId = `__optimistic_${crypto.randomUUID()}`;
        const patchList = dispatch(
          coachApi.util.updateQueryData('getTrainingPlans', undefined, (draft) => {
            draft.unshift({
              id: optimisticId,
              clientId,
              trainerId: '',
              name,
              status: 'ACTIVE',
              createdByAi: false,
              createdAt: new Date().toISOString(),
              exerciseCount: exercises?.length ?? 0,
            });
          }),
        );
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            coachApi.util.updateQueryData('getTrainingPlans', undefined, (draft) => {
              const optimisticIndex = draft.findIndex((plan) => plan.id === optimisticId);
              if (optimisticIndex !== -1) {
                draft[optimisticIndex] = created;
                return;
              }
              const index = draft.findIndex((plan) => plan.id === created.id);
              if (index === -1) {
                draft.unshift(created);
              } else {
                draft[index] = created;
              }
            }),
          );
          if (exercises !== undefined) {
            dispatch(
              coachApi.util.upsertQueryData('getTrainingPlan', created.id, {
                ...created,
                exercises,
              }),
            );
          }
        } catch {
          patchList.undo();
        }
      },
      invalidatesTags: [],
    }),
    updateTrainingPlan: builder.mutation<
      TrainingPlanWithExercises,
      { id: string; data: { name?: string; status?: TrainingPlanStatus; exercises?: Exercise[] } }
    >({
      query: ({ id, data }) => ({ url: `/training-plans/${id}`, method: 'PATCH', data }),
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        const patchList = dispatch(
          coachApi.util.updateQueryData('getTrainingPlans', undefined, (draft) => {
            const index = draft.findIndex((plan) => plan.id === id);
            if (index !== -1) {
              if (data.name !== undefined) draft[index].name = data.name;
              if (data.status !== undefined) draft[index].status = data.status;
            }
          }),
        );
        const patchDetail =
          data.name !== undefined || data.status !== undefined
            ? dispatch(
                coachApi.util.updateQueryData('getTrainingPlan', id, (draft) => {
                  if (data.name !== undefined) draft.name = data.name;
                  if (data.status !== undefined) draft.status = data.status;
                }),
              )
            : undefined;
        try {
          const { data: updated } = await queryFulfilled;
          const plan: TrainingPlan = {
            id: updated.id,
            clientId: updated.clientId,
            trainerId: updated.trainerId,
            name: updated.name,
            status: updated.status,
            createdByAi: updated.createdByAi,
            createdAt: updated.createdAt,
            updatedAt: updated.updatedAt,
            exerciseCount: updated.exerciseCount ?? updated.exercises.length,
          };
          dispatch(
            coachApi.util.updateQueryData('getTrainingPlans', undefined, (draft) => {
              const index = draft.findIndex((p) => p.id === id);
              if (index !== -1) {
                draft[index] = { ...draft[index], ...plan };
              }
            }),
          );
          dispatch(coachApi.util.upsertQueryData('getTrainingPlan', id, updated));
        } catch {
          patchList.undo();
          patchDetail?.undo();
        }
      },
      invalidatesTags: [],
    }),
    deleteTrainingPlan: builder.mutation<void, string>({
      query: (id) => ({ url: `/training-plans/${id}`, method: 'DELETE' }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          coachApi.util.updateQueryData('getTrainingPlans', undefined, (draft) => {
            const index = draft.findIndex((plan) => plan.id === id);
            if (index !== -1) draft.splice(index, 1);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [],
    }),

    getChallenges: builder.query<ClientChallenge[], { clientId: string } & ApiMetaArg>({
      query: ({ clientId, meta }) => ({
        url: '/challenges',
        params: { clientId },
        meta,
      }),
      providesTags: (_result, _error, { clientId }) => [
        { type: 'Challenges', id: clientId },
      ],
    }),
    createChallenge: builder.mutation<
      ClientChallenge,
      {
        clientId: string;
        title: string;
        description?: string;
        coachMessage?: string;
        coinReward: number;
        frequency: string;
        weeklyTarget?: number;
        dueAt?: string;
      }
    >({
      query: (data) => ({ url: '/challenges', method: 'POST', data }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Challenges', id: clientId },
        { type: 'ClientCoins', id: clientId },
      ],
    }),
    updateChallenge: builder.mutation<
      ClientChallenge,
      {
        id: string;
        clientId: string;
        data: Partial<{
          title: string;
          description: string;
          coachMessage: string;
          coinReward: number;
          weeklyTarget: number;
          dueAt: string | null;
          status: string;
        }>;
      }
    >({
      query: ({ id, data }) => ({ url: `/challenges/${id}`, method: 'PATCH', data }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Challenges', id: clientId },
      ],
    }),
    archiveChallenge: builder.mutation<ClientChallenge, { id: string; clientId: string }>({
      query: ({ id }) => ({ url: `/challenges/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Challenges', id: clientId },
      ],
    }),
    getClientCoins: builder.query<ClientCoinsData, { clientId: string } & ApiMetaArg>({
      query: ({ clientId, meta }) => ({ url: `/clients/${clientId}/coins`, meta }),
      providesTags: (_result, _error, { clientId }) => [
        { type: 'ClientCoins', id: clientId },
      ],
    }),
    toggleCoins: builder.mutation<
      { coinsEnabled: boolean },
      { clientId: string; coinsEnabled: boolean }
    >({
      query: ({ clientId, coinsEnabled }) => ({
        url: `/clients/${clientId}/coins`,
        method: 'PATCH',
        data: { coinsEnabled },
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientCoins', id: clientId },
      ],
    }),
    redeemCoins: builder.mutation<
      ClientCoinsData,
      { clientId: string; amount: number; note?: string }
    >({
      query: ({ clientId, amount, note }) => ({
        url: `/clients/${clientId}/coins/redeem`,
        method: 'POST',
        data: { amount, note },
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientCoins', id: clientId },
      ],
    }),

    getClientPackages: builder.query<ClientPackage[], { clientId: string } & ApiMetaArg>({
      query: ({ clientId, meta }) => ({
        url: `/clients/${clientId}/packages`,
        meta,
      }),
      providesTags: (_result, _error, { clientId }) => [
        { type: 'ClientPackages', id: clientId },
      ],
    }),
    createClientPackage: builder.mutation<
      ClientPackage,
      {
        clientId: string;
        name: string;
        totalSessions: number;
        priceCents: number;
        paidAt?: string;
        expiresAt?: string;
      }
    >({
      query: ({ clientId, ...data }) => ({
        url: `/clients/${clientId}/packages`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientPackages', id: clientId },
      ],
    }),
    updateClientPackage: builder.mutation<
      ClientPackage,
      {
        clientId: string;
        id: string;
        data: Partial<{
          name: string;
          totalSessions: number;
          priceCents: number;
          paidAt: string | null;
          expiresAt: string | null;
        }>;
      }
    >({
      query: ({ clientId, id, data }) => ({
        url: `/clients/${clientId}/packages/${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientPackages', id: clientId },
      ],
    }),
    useClientPackageSession: builder.mutation<
      ClientPackage,
      { clientId: string; id: string }
    >({
      query: ({ clientId, id }) => ({
        url: `/clients/${clientId}/packages/${id}/use-session`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientPackages', id: clientId },
      ],
    }),
    restoreClientPackageSession: builder.mutation<
      ClientPackage,
      { clientId: string; id: string }
    >({
      query: ({ clientId, id }) => ({
        url: `/clients/${clientId}/packages/${id}/restore-session`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientPackages', id: clientId },
      ],
    }),
    deleteClientPackage: builder.mutation<void, { clientId: string; id: string }>({
      query: ({ clientId, id }) => ({
        url: `/clients/${clientId}/packages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'ClientPackages', id: clientId },
      ],
    }),

    getTrainerProfile: builder.query<TrainerProfile, ApiMetaArg | void>({
      query: (arg) => ({ url: '/trainers/me', meta: arg?.meta }),
      providesTags: ['Trainer'],
    }),
    updateTrainerProfile: builder.mutation<
      TrainerProfile,
      Partial<Pick<Trainer, 'name' | 'workingHours'>>
    >({
      query: (data) => ({ url: '/trainers/me', method: 'PATCH', data }),
      invalidatesTags: ['Trainer'],
    }),

    getRetention: builder.query<RetentionAnalytics, ApiMetaArg | void>({
      query: (arg) => ({ url: '/analytics/retention', meta: arg?.meta }),
      providesTags: ['Retention'],
    }),
    getFunnel: builder.query<FunnelAnalytics, ApiMetaArg | void>({
      query: (arg) => ({ url: '/analytics/funnel', meta: arg?.meta }),
      providesTags: ['Funnel'],
    }),

    searchMovements: builder.query<
      Movement[],
      { q?: string } & ApiMetaArg | void
    >({
      query: (arg) => ({
        url: '/movements',
        params: arg ? { q: arg.q } : undefined,
        meta: arg?.meta,
      }),
      providesTags: ['Movements'],
    }),
    matchMovements: builder.mutation<MovementMatch[], { names: string[] }>({
      query: (data) => ({ url: '/movements/match', method: 'POST', data }),
    }),
    createMovement: builder.mutation<
      Movement,
      { name: string; videoId?: string }
    >({
      query: (data) => ({ url: '/movements', method: 'POST', data }),
      invalidatesTags: ['Movements', 'Videos'],
    }),
    updateMovement: builder.mutation<
      Movement,
      { id: string; data: { name?: string; videoId?: string | null } }
    >({
      query: ({ id, data }) => ({ url: `/movements/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['Movements', 'Videos'],
    }),
    deleteMovement: builder.mutation<void, string>({
      query: (id) => ({ url: `/movements/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Movements', 'Videos'],
    }),

    reserveVideoUpload: builder.mutation<
      { videoId: string; uploadUrl: string },
      void
    >({
      query: () => ({ url: '/videos/uploads', method: 'POST' }),
    }),
    completeVideoUpload: builder.mutation<
      { videoId: string },
      { id: string; durationSec: number }
    >({
      query: ({ id, durationSec }) => ({
        url: `/videos/${id}/complete`,
        method: 'POST',
        data: { durationSec },
      }),
      invalidatesTags: ['Videos'],
    }),
    listVideos: builder.query<VideoListItem[], ApiMetaArg | void>({
      query: (arg) => ({ url: '/videos', meta: arg?.meta }),
      providesTags: ['Videos'],
    }),
    syncVideosFromDisk: builder.mutation<VideoListItem[], void>({
      query: () => ({ url: '/videos/sync', method: 'POST' }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: added } = await queryFulfilled;
          if (!added.length) return;
          dispatch(
            coachApi.util.updateQueryData('listVideos', undefined, (draft) => {
              for (const item of added) {
                if (!draft.some((video) => video.id === item.id)) {
                  draft.push(item);
                }
              }
            }),
          );
        } catch {
          // Keep the cached list if Disk sync fails.
        }
      },
    }),
    deleteVideo: builder.mutation<void, string>({
      query: (id) => ({ url: `/videos/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Videos', 'Movements'],
    }),
    playback: builder.query<{ href: string }, string>({
      query: (id) => ({ url: `/videos/${id}/playback` }),
    }),
  }),
});

export const {
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useGetClientQuery,
  useUpdateClientMutation,
  useInviteClientMutation,
  useGetBookingsQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useDeleteBookingMutation,
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useCompleteTaskMutation,
  useDeleteTaskMutation,
  useGetLeadsQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useConvertLeadMutation,
  useDeleteLeadMutation,
  useGetMeasurementsQuery,
  useCreateMeasurementMutation,
  useUpdateMeasurementMutation,
  useDeleteMeasurementMutation,
  useGenerateInsightMutation,
  useGeneratePlanMutation,
  useGetTrainingPlansQuery,
  useGetTrainingPlanQuery,
  useLazyGetTrainingPlanQuery,
  useCreateTrainingPlanMutation,
  useUpdateTrainingPlanMutation,
  useDeleteTrainingPlanMutation,
  useGetChallengesQuery,
  useCreateChallengeMutation,
  useUpdateChallengeMutation,
  useArchiveChallengeMutation,
  useGetClientCoinsQuery,
  useToggleCoinsMutation,
  useRedeemCoinsMutation,
  useGetClientPackagesQuery,
  useCreateClientPackageMutation,
  useUpdateClientPackageMutation,
  useUseClientPackageSessionMutation,
  useRestoreClientPackageSessionMutation,
  useDeleteClientPackageMutation,
  useGetTrainerProfileQuery,
  useUpdateTrainerProfileMutation,
  useGetRetentionQuery,
  useGetFunnelQuery,
  useSearchMovementsQuery,
  useMatchMovementsMutation,
  useCreateMovementMutation,
  useUpdateMovementMutation,
  useDeleteMovementMutation,
  useReserveVideoUploadMutation,
  useCompleteVideoUploadMutation,
  useListVideosQuery,
  useSyncVideosFromDiskMutation,
  useDeleteVideoMutation,
  useLazyPlaybackQuery,
} = coachApi;
